import React, { useState } from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { primary } from "../../../src/constants/theme";
import { MemoizedFooter } from "../../../src/layouts/footer";
import { MemoizedHeader } from "../../../src/layouts/header";
import MemoizedSearchHeader from "../../../src/modules/Vehicles/searchHeader.jsx";
import Breadcrumbs from "../../../src/components/common/Breadcrumbs";
import { SecondaryHeader } from "../../../src/components/common";
import { useMobile } from "../../../src/utils/findViewport";
import { MemoizedPricingLeadCapture } from "../../components/PricingPage/PricingLeadCapture";
import PricingAtfContent from "../../components/PricingPage/PricingAtfContent";
import PricingPopularCities from "../../components/PricingPage/PricingPopularCities";
import PricingEvExpertsBanner from "../../components/PricingPage/PricingEvExpertsBanner";
import PricingVehicleSpecsTabs from "../../components/PricingPage/PricingVehicleSpecsTabs";
import PricingSelectCityModal from "../../components/PricingPage/PricingSelectCityModal";
import LoanFormPopupV2 from "../../Drawer/LoanFormPopupV2";
import { generatePricingFAQ } from "../../components/PricingPage/pricingFAQTemplate";
import { vehiclePromiseList } from "../../constants/vehiclesPromises";

// Below-fold sections: code-split to improve LCP/TBT (Web Vitals)
const VehicleLatestList = dynamic(
  () => import("../Home/VehiclesV2/VehicleLatestList").then((m) => m.default),
  { ssr: true }
);
const FAQ = dynamic(() => import("../../SDK/FAQ/faq").then((m) => m.default), {
  ssr: true,
});

// Format city slug for display (e.g. "delhi-ncr" -> "Delhi-NCR", "pune" -> "Pune")
const formatCityDisplay = (slug) => {
  if (!slug) return "";
  if (slug.toLowerCase() === "delhi-ncr") return "Delhi-NCR";
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const PricingPage = ({
  title = "Vehicle Pricing",
  metaContent = [],
  canonicalUrl = "",
  BREADCRUMB_ITEMS = [],
  BREADCRUMB_SCHEMA = {},
  location = "",
  H1_HEADING = "",
  vehicleDataLocal = [],
  KEY_SPECIFICATION_HEADER = "",
  LATEST_VEHICLES_HEADER = "",
  latestVehiclesInfo = [],
  vehicleName = "",
  make = "",
  model = "",
  minPrice = "",
  maxPrice = "",
  gvw = "",
  payload = "",
  range = "",
  brand = "",
  modelSlug = "",
  citySlug = "",
  availableCities = [],
}) => {
  const isMobile = useMobile();
  const [cityModalOpen, setCityModalOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState("section-price");
  const showSelectCity = availableCities?.length > 0 && brand && modelSlug;

  const vehicleDisplayName = model || vehicleDataLocal?.[0]?.vehicle_name || vehicleDataLocal?.[0]?.name || "";
  const secondaryHeaderItems = [
    ...(vehicleDisplayName && brand && modelSlug
      ? [{ label: `${vehicleDataLocal?.[0]?.brand} ${vehicleDisplayName}`, href: `/electric-commercial-vehicles/${brand}/${modelSlug}` }]
      : []),
    { label: "Price", sectionId: "section-price" },
    { label: "Specification", sectionId: "section-specification" },
    { label: "Summary", sectionId: "section-summary" },
    { label: "Disclaimer", sectionId: "section-disclaimer" },
  ];

  const handleCitySelect = (newCitySlug) => {
    if (!newCitySlug || newCitySlug === citySlug) return;
    // Full page navigation so the new URL loads as pre-built HTML (not client-side)
    window.location.href = `/electric-commercial-vehicles/${brand}/${modelSlug}/price/${newCitySlug}`;
  };
  // Map vehicle_path_id to vehiclePromiseList keys
  const getVehiclePromiseKey = () => {
    const vehiclePathId = vehicleDataLocal?.[0]?.vehicle_path_id || "";
    const pathIdLower = vehiclePathId.toLowerCase();
    
    // Map vehicle_path_id to vehiclePromiseList keys
    const mapping = {
      "treo-zor": "treoZor",
      "zor-grand": "zorGrand",
      "e-xtra-fx": "piaggioFX",
      "e-xtra-fx-max": "piaggioFXMax",
      "eltra": "greavesEltra",
      "touro-max": "touroMax",
      "hiloaddv": "hiLoadDV",
      "hiloaddv170": "hiLoadDV",
      "hiloadpv": "hiLoadPV",
      "neev": "neev",
      "super-auto": "montraePV",
      "ape-e-city-fx": "piaggioEcityFx",
      "re-e-tec": "bajajAutoRikshaw",
      "treo-plus": "MahindraTreoPlus",
    };
    
    return mapping[pathIdLower] || null;
  };

  const vehiclePromiseKey = getVehiclePromiseKey();
  const promisesData = vehiclePromiseKey ? vehiclePromiseList[vehiclePromiseKey] : null;
  return (
    <>
      <Head>
        <title>{title}</title>
        {metaContent?.map((content, index) => (
          <meta
            key={index}
            name={content?.name ? content?.name : ""}
            property={content?.property ? content?.property : ""}
            content={content?.content}
          />
        ))}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {BREADCRUMB_SCHEMA?.itemListElement?.length > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(BREADCRUMB_SCHEMA),
            }}
            defer
          />
        )}
      </Head>

      <body>
        <div>
          {/* Desktop Header */}
          <div className="hidden md:block">
            <MemoizedHeader scrollWithPage={true}/>
          </div>
          <div className="block md:hidden">
            <MemoizedSearchHeader isProductPage={true} showSearch={false}/>
          </div>
          {secondaryHeaderItems.length > 0 && (
              <SecondaryHeader
                items={secondaryHeaderItems}
                activeSectionId={activeSectionId}
                onActiveSectionChange={setActiveSectionId}
              />
          )}
          {/* Breadcrumb + Select City: desktop only, hidden in mobile view */}
          {((Array.isArray(BREADCRUMB_ITEMS) && BREADCRUMB_ITEMS.length > 0) || showSelectCity) && (
            <div className="hidden w-full md:w-[84%] mx-auto px-3 md:px-0 mt-5 mb-5 md:flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-3">
              {Array.isArray(BREADCRUMB_ITEMS) && BREADCRUMB_ITEMS.length > 0 && (
                <div className="hidden md:block flex-1 min-w-0">
                  <Breadcrumbs items={BREADCRUMB_ITEMS} />
                </div>
              )}
              {showSelectCity && (
                <div className="hidden md:flex items-center shrink-0">
                  <button
                    type="button"
                    onClick={() => setCityModalOpen(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-[#024670] focus:ring-offset-2 transition-colors"
                    style={{
                      background: primary?.lightPink,
                      color: primary?.mainText,
                    }}
                    aria-haspopup="dialog"
                    aria-expanded={cityModalOpen}
                  >
                    <span className="whitespace-nowrap truncate max-w-[120px] sm:max-w-none">
                      {formatCityDisplay(citySlug)}
                    </span>
                    <ExpandMoreIcon sx={{ fontSize: 20, color: primary?.main }} />
                  </button>
                </div>
              )}
            </div>
          )}
          <PricingSelectCityModal
            isOpen={cityModalOpen}
            onClose={() => setCityModalOpen(false)}
            currentCitySlug={citySlug}
            availableCities={availableCities}
            onSelectCity={handleCitySelect}
          />
          {H1_HEADING && !isMobile && (
            <div className="hidden md:block w-full md:w-[84%] mx-auto mb-6 px-3 md:px-0">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {H1_HEADING}
              </h1>
            </div>
          )}
          {/* <div className="relative w-full">
            <div className="absolute top-0 left-0 z-[120] block md:hidden w-full">
              {vehicleDataLocal?.[0] && <WebShare vehicleData={vehicleDataLocal[0]} />}
              <BookVehicleDrawer />
            </div>
          </div> */}

          <div id="section-price">
          <MemoizedPricingLeadCapture 
            vehicleDataLocal={vehicleDataLocal}
            location={location}
            minPrice={minPrice}
            maxPrice={maxPrice}
            heading={H1_HEADING}
            isMobile={isMobile}
            selectCityConfig={
              showSelectCity
                ? {
                    citySlug,
                    formatCityDisplay,
                    onOpenModal: () => setCityModalOpen(true),
                    cityModalOpen,
                  }
                : null
            }
          />
          </div>

          <LoanFormPopupV2 />
          
          {promisesData && (
            <div id="section-specification">
            <PricingVehicleSpecsTabs 
              promisesData={promisesData}
              headerInfo={KEY_SPECIFICATION_HEADER || "Key Specifications"}
            />
            </div>
          )}
          
          <div id="section-summary">
          <PricingAtfContent
            vehicleData={vehicleDataLocal?.[0]}
            location={location}
            make={make || vehicleDataLocal?.[0]?.brand}
            model={model || vehicleDataLocal?.[0]?.name}
            minPrice={minPrice}
            maxPrice={maxPrice}
            gvw={gvw}
            payload={payload}
            range={range}
          />
          
          <PricingPopularCities
            vehicleBrand={make || vehicleDataLocal?.[0]?.brand}
            vehicleName={model || vehicleDataLocal?.[0]?.vehicle_name || vehicleDataLocal?.[0]?.name}
            brand={brand}
            model={modelSlug}
          />
          </div>
          
          {latestVehiclesInfo && latestVehiclesInfo.length > 0 && (
            <div className="w-full md:w-[84%] mx-auto px-3 md:px-0 py-8 md:py-12 bg-white">
              {LATEST_VEHICLES_HEADER && (
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">
                  {LATEST_VEHICLES_HEADER}
                </h2>
              )}
              <VehicleLatestList 
                headerInfo={LATEST_VEHICLES_HEADER || undefined}
                latestVehiclesInfo={latestVehiclesInfo}
                hideHeader={true}
              />
            </div>
          )}
          
          <div className="w-full md:w-[84%] mx-auto px-3 md:px-0 py-8 md:py-12 bg-white">
            <div className="w-full">
              <FAQ
                faqList={generatePricingFAQ({
                  make: make || vehicleDataLocal?.[0]?.brand,
                  model: model || vehicleDataLocal?.[0]?.vehicle_name || vehicleDataLocal?.[0]?.name,
                  city: location?.split(",")?.[0]?.trim() || "",
                  state: location?.split(",")?.[1]?.trim() || "",
                  mileage: vehicleDataLocal?.[0]?.range || "",
                  minPrice: minPrice ? `${minPrice} Lakh` : "",
                  maxPrice: maxPrice ? `${maxPrice} Lakh` : "",
                  chargingTime: vehicleDataLocal?.[0]?.chargingtime || "",
                })}
                showItems={6}
                headText={`Frequently Asked Questions about ${make || vehicleDataLocal?.[0]?.brand || ""} ${model || vehicleDataLocal?.[0]?.vehicle_name || vehicleDataLocal?.[0]?.name || ""}`}
              />
            </div>
          </div>
          
          <PricingEvExpertsBanner />
          
          <div id="section-disclaimer" className="w-full md:w-[84%] mx-auto px-3 md:px-0 py-8 md:py-12 bg-white">
            <div className="w-full">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8">
                Disclaimer
              </h2>
              <p className="text-sm md:text-base text-gray-500 leading-relaxed">
                *The prices mentioned are ex-showroom and are subject to change without prior notice. On-road price includes insurance, registration, and other local taxes which vary by state. Range is based on standard testing conditions. Actual range may vary based on driving habits, load, and terrain.
              </p>
            </div>
          </div>

          {/* Mobile breadcrumbs at bottom */}
          {Array.isArray(BREADCRUMB_ITEMS) && BREADCRUMB_ITEMS.length > 0 && (
            <div className="block md:hidden w-full px-3 py-4 bg-white">
              <Breadcrumbs items={BREADCRUMB_ITEMS} />
            </div>
          )}

          <MemoizedFooter />
        </div>
      </body>
    </>
  );
};

export default PricingPage;
